import { useEffect } from "react";
import { Delete, ArrowRight } from "lucide-react";

// ── PIN display ──────────────────────────────────────────────────────────────

export function PinDisplay({
  length,
  filled,
  error,
}: {
  length: number;
  filled: number;
  error: boolean;
}) {
  return (
    <div className="flex items-center justify-center gap-2">
      {Array.from({ length }).map((_, i) => (
        <div
          key={i}
          className="pin-display-cell"
          style={{
            fontFamily: "'DM Mono', monospace",
            width: "2.5rem",
            height: "2.5rem",
            borderRadius: "50%",
            border: `1.5px solid ${i < filled ? (error ? "var(--login-error)" : "var(--login-accent)") : "var(--login-border)"}`,
            background: "var(--login-surface)",
            color: error ? "var(--login-error)" : "var(--login-accent)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.25rem",
            transition: "border-color 0.15s, color 0.15s",
          }}
        >
          {i < filled ? (
            <span
              className="pin-display-dot"
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: "currentColor",
                flex: "none",
              }}
            />
          ) : (
            <div
              style={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                background: "var(--login-border)",
              }}
            />
          )}
        </div>
      ))}
    </div>
  );
}

// ── PIN pad ──────────────────────────────────────────────────────────────────

const PIN_ROWS = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
  ["⌫", "0", "↵"],
];

export function PinPad({
  onDigit,
  onBackspace,
  onConfirm,
  disabled,
  active = true,
}: {
  onDigit: (d: string) => void;
  onBackspace: () => void;
  onConfirm: () => void;
  disabled: boolean;
  /** When true, captures keyboard events globally. Default: true. */
  active?: boolean;
}) {
  // Keyboard capture
  useEffect(() => {
    if (!active) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (disabled) return;
      if (
        document.activeElement instanceof HTMLInputElement ||
        document.activeElement instanceof HTMLTextAreaElement
      )
        return;
      if (
        e.key === "Enter" &&
        document.activeElement instanceof HTMLButtonElement
      )
        return;
      if (e.key >= "0" && e.key <= "9") {
        e.preventDefault();
        onDigit(e.key);
      } else if (e.key === "Backspace") {
        e.preventDefault();
        onBackspace();
      } else if (e.key === "Enter") {
        e.preventDefault();
        onConfirm();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [active, disabled, onDigit, onBackspace, onConfirm]);

  return (
    <div className="pin-pad" style={{ display: "grid", gap: "0.5rem" }}>
      {PIN_ROWS.map((row, ri) => (
        <div
          key={ri}
          style={{ display: "flex", justifyContent: "center", gap: "0.5rem" }}
        >
          {row.map((key) => {
            const isAction = key === "⌫" || key === "↵";
            return (
              <button
                key={key}
                type="button"
                disabled={disabled}
                aria-label={
                  key === "⌫" ? "Apagar" : key === "↵" ? "Confirmar" : undefined
                }
                onClick={() => {
                  if (key === "⌫") onBackspace();
                  else if (key === "↵") onConfirm();
                  else onDigit(key);
                }}
                style={{
                  fontFamily: isAction
                    ? "'DM Sans', sans-serif"
                    : "'DM Mono', monospace",
                  width: "3.5rem",
                  height: "3.5rem",
                  background: "var(--login-surface)",
                  border: "1.5px solid var(--login-border)",
                  color: isAction ? "var(--login-muted)" : "var(--login-text)",
                  fontSize: isAction ? "1rem" : "1.125rem",
                  cursor: disabled ? "not-allowed" : "pointer",
                  opacity: disabled ? 0.4 : 1,
                  transition:
                    "background 0.15s, border-color 0.15s, transform 0.18s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onMouseDown={(e) => {
                  if (!disabled) {
                    (e.currentTarget as HTMLButtonElement).style.background =
                      "var(--login-border)";
                    (e.currentTarget as HTMLButtonElement).style.borderColor =
                      "var(--login-accent)";
                  }
                }}
                onMouseUp={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    "var(--login-surface)";
                  (e.currentTarget as HTMLButtonElement).style.borderColor =
                    "var(--login-border)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    "var(--login-surface)";
                  (e.currentTarget as HTMLButtonElement).style.borderColor =
                    "var(--login-border)";
                }}
              >
                {key === "⌫" ? (
                  <Delete size={20} strokeWidth={1.8} aria-hidden="true" />
                ) : key === "↵" ? (
                  <ArrowRight size={20} strokeWidth={1.8} aria-hidden="true" />
                ) : (
                  key
                )}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
